<template>
  <div class="cs-page">
    <!-- Loading -->
    <div
      v-if="loadingData"
      class="cs-loading"
    >
      <div class="cs-loading__spinner" />
      <p class="mb-0 text-muted">
        Loading company…
      </p>
    </div>

    <!-- The fetch failed. Say so, rather than rendering an empty page that reads
         as "this company has no data". -->
    <div
      v-else-if="loadError"
      class="alert alert-warning m-3 d-flex align-items-center"
      role="alert"
    >
      <feather-icon
        icon="AlertTriangleIcon"
        size="20"
        class="mr-2"
      />
      <div>{{ loadError }}</div>
    </div>

    <template v-else>
      <!-- Hero -->
      <section class="cs-hero">
        <div class="cs-hero__glow" />
        <div class="cs-hero__main">
          <div class="cs-hero__identity">
            <div
              class="cs-hero__avatar"
              :style="{ background: avatarColor }"
            >
              {{ initials(companyName) }}
            </div>
            <div class="cs-hero__titles">
              <h2 class="cs-hero__title">
                {{ companyName || 'Company' }}
              </h2>
              <span
                v-if="secondaryName"
                class="cs-hero__title-ar"
              >
                {{ secondaryName }}
              </span>
              <div class="cs-hero__meta">
                <span class="cs-hero__chip">
                  <feather-icon
                    icon="HashIcon"
                    size="12"
                  />
                  {{ company.id }}
                </span>
                <span
                  v-if="company.CR_num"
                  class="cs-hero__chip"
                >
                  <feather-icon
                    icon="FileTextIcon"
                    size="12"
                  />
                  CR {{ company.CR_num }}
                </span>
                <span
                  class="cs-cr"
                  :class="`cs-cr--${crStatus.variant}`"
                >
                  <feather-icon
                    :icon="crStatus.icon"
                    size="12"
                  />
                  {{ crStatus.label }}
                </span>
              </div>
            </div>
          </div>
          <div class="cs-hero__actions">
            <button
              type="button"
              class="cs-btn cs-btn--ghost"
              @click="goBack"
            >
              <feather-icon
                icon="ArrowLeftIcon"
                size="16"
              />
              Back
            </button>
          </div>
        </div>
      </section>

      <div class="cs-grid">
        <!-- Company details -->
        <section class="cs-card cs-card--span2">
          <header class="cs-card__head">
            <span class="cs-card__head-icon cs-card__head-icon--primary">
              <feather-icon
                icon="HomeIcon"
                size="18"
              />
            </span>
            <div>
              <h3 class="cs-card__title">
                Company Details
              </h3>
              <p class="cs-card__subtitle">
                Registration &amp; contact information.
              </p>
            </div>
          </header>

          <div class="cs-card__body">
            <div class="cs-info-grid">
              <a
                class="cs-info"
                :class="{ 'cs-info--plain': !company.email }"
                :href="company.email ? `mailto:${company.email}` : null"
              >
                <span class="cs-info__icon cs-info__icon--info">
                  <feather-icon
                    icon="MailIcon"
                    size="15"
                  />
                </span>
                <div class="cs-info__content">
                  <span class="cs-info__label">Email</span>
                  <span class="cs-info__value cs-info__value--truncate">{{ company.email || '—' }}</span>
                </div>
              </a>

              <a
                class="cs-info"
                :class="{ 'cs-info--plain': !company.phone }"
                :href="company.phone ? `tel:${company.phone}` : null"
              >
                <span class="cs-info__icon cs-info__icon--primary">
                  <feather-icon
                    icon="PhoneIcon"
                    size="15"
                  />
                </span>
                <div class="cs-info__content">
                  <span class="cs-info__label">Phone</span>
                  <span class="cs-info__value">{{ company.phone || '—' }}</span>
                </div>
              </a>

              <div class="cs-info cs-info--plain">
                <span class="cs-info__icon cs-info__icon--primary">
                  <feather-icon
                    icon="FileTextIcon"
                    size="15"
                  />
                </span>
                <div class="cs-info__content">
                  <span class="cs-info__label">CR Number</span>
                  <span class="cs-info__value">{{ company.CR_num || '—' }}</span>
                </div>
              </div>

              <div class="cs-info cs-info--plain">
                <span class="cs-info__icon cs-info__icon--warning">
                  <feather-icon
                    icon="CalendarIcon"
                    size="15"
                  />
                </span>
                <div class="cs-info__content">
                  <span class="cs-info__label">CR End Date</span>
                  <span class="cs-info__value">{{ formatDate(company.CR_end_date) }}</span>
                </div>
              </div>

              <!-- Karim, 2 Sep: "add client rating" — a judgement call the owner
                   or a manager sets, not something computed. -->
              <div class="cs-info cs-info--plain">
                <span
                  class="cs-info__icon"
                  :class="`cs-info__icon--tier-${company.tier || 'none'}`"
                >
                  <feather-icon
                    icon="AwardIcon"
                    size="15"
                  />
                </span>
                <div class="cs-info__content">
                  <span class="cs-info__label">Rating</span>
                  <select
                    class="cs-tier-select"
                    :class="`cs-tier-select--${company.tier || 'none'}`"
                    :value="company.tier || ''"
                    :disabled="savingTier"
                    @change="setTier($event.target.value)"
                  >
                    <option value="">
                      Not rated
                    </option>
                    <option value="gold">
                      Gold
                    </option>
                    <option value="silver">
                      Silver
                    </option>
                    <option value="bronze">
                      Bronze
                    </option>
                  </select>
                </div>
              </div>

              <div
                v-if="company.crEntityNumber"
                class="cs-info cs-info--plain"
              >
                <span class="cs-info__icon cs-info__icon--accent">
                  <feather-icon
                    icon="HashIcon"
                    size="15"
                  />
                </span>
                <div class="cs-info__content">
                  <span class="cs-info__label">CR Entity Number</span>
                  <span class="cs-info__value">{{ company.crEntityNumber }}</span>
                </div>
              </div>

              <div
                v-if="postalCode"
                class="cs-info cs-info--plain"
              >
                <span class="cs-info__icon cs-info__icon--info">
                  <feather-icon
                    icon="NavigationIcon"
                    size="15"
                  />
                </span>
                <div class="cs-info__content">
                  <span class="cs-info__label">Postal Code</span>
                  <span class="cs-info__value">{{ postalCode }}</span>
                </div>
              </div>

              <div
                v-if="company.audit_license_expiration"
                class="cs-info cs-info--plain"
              >
                <span class="cs-info__icon cs-info__icon--danger">
                  <feather-icon
                    icon="ShieldIcon"
                    size="15"
                  />
                </span>
                <div class="cs-info__content">
                  <span class="cs-info__label">Audit License Expiration</span>
                  <span class="cs-info__value">{{ formatDate(company.audit_license_expiration) }}</span>
                </div>
              </div>

              <div
                v-if="company.survialnce"
                class="cs-info cs-info--plain"
              >
                <span class="cs-info__icon cs-info__icon--accent">
                  <feather-icon
                    icon="EyeIcon"
                    size="15"
                  />
                </span>
                <div class="cs-info__content">
                  <span class="cs-info__label">Surveillance</span>
                  <span class="cs-info__value">{{ company.survialnce }}</span>
                </div>
              </div>

              <div
                class="cs-info cs-info--plain cs-info--wide"
              >
                <span class="cs-info__icon cs-info__icon--warning">
                  <feather-icon
                    icon="MapPinIcon"
                    size="15"
                  />
                </span>
                <div class="cs-info__content">
                  <span class="cs-info__label">Address</span>
                  <span class="cs-info__value">{{ company.address || '—' }}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Activity -->
        <section
          v-if="activityList.length"
          class="cs-card cs-card--span2"
        >
          <header class="cs-card__head">
            <span class="cs-card__head-icon cs-card__head-icon--info">
              <feather-icon
                icon="ActivityIcon"
                size="18"
              />
            </span>
            <div>
              <h3 class="cs-card__title">
                Business Activities
                <span class="cs-card__count">{{ activityList.length }}</span>
              </h3>
              <p class="cs-card__subtitle">
                Registered scope of activities.
              </p>
            </div>
          </header>

          <div class="cs-card__body">
            <div class="cs-tags">
              <span
                v-for="(act, i) in activityList"
                :key="i"
                class="cs-tag"
              >
                <feather-icon
                  icon="CheckCircleIcon"
                  size="13"
                />
                {{ act }}
              </span>
            </div>
          </div>
        </section>

        <!-- Delegators -->
        <section class="cs-card cs-card--span2">
          <header class="cs-card__head">
            <span class="cs-card__head-icon cs-card__head-icon--success">
              <feather-icon
                icon="UsersIcon"
                size="18"
              />
            </span>
            <div>
              <h3 class="cs-card__title">
                Delegators
                <span class="cs-card__count">{{ delegators.length }}</span>
              </h3>
              <p class="cs-card__subtitle">
                People authorized to act for this company.
              </p>
            </div>
          </header>

          <div class="cs-card__body">
            <div
              v-if="!delegators.length"
              class="cs-empty"
            >
              <div class="cs-empty__icon">
                <feather-icon
                  icon="UserXIcon"
                  size="26"
                />
              </div>
              <p class="mb-0 text-muted">
                No delegators found for this company.
              </p>
            </div>

            <div
              v-else
              class="cs-deleg-grid"
            >
              <article
                v-for="(delegator, index) in delegators"
                :key="delegator.id || index"
                class="cs-deleg"
              >
                <div class="cs-deleg__top">
                  <div
                    class="cs-deleg__avatar"
                    :style="{ background: colorFor(delegator.id || index) }"
                  >
                    {{ initials(delegator.name) }}
                  </div>
                  <!-- Karim, 2 Sep: "I want to press on the delegator and he opens
                       in a new tab." -->
                  <a
                    v-if="delegator.id"
                    :href="contactHref(delegator.id)"
                    target="_blank"
                    rel="noopener"
                    class="cs-deleg__name cs-deleg__name--link"
                  >
                    {{ delegator.name || 'Unnamed delegator' }}
                  </a>
                  <h4
                    v-else
                    class="cs-deleg__name"
                  >
                    {{ delegator.name || 'Unnamed delegator' }}
                  </h4>
                </div>

                <div class="cs-deleg__body">
                  <a
                    v-if="delegator.email"
                    :href="`mailto:${delegator.email}`"
                    class="cs-line"
                    :title="delegator.email"
                  >
                    <span class="cs-line__icon cs-line__icon--info">
                      <feather-icon
                        icon="MailIcon"
                        size="12"
                      />
                    </span>
                    <span class="cs-line__text">{{ delegator.email }}</span>
                  </a>
                  <a
                    v-if="delegator.phone"
                    :href="`tel:${delegator.phone}`"
                    class="cs-line"
                  >
                    <span class="cs-line__icon cs-line__icon--primary">
                      <feather-icon
                        icon="PhoneIcon"
                        size="12"
                      />
                    </span>
                    <span class="cs-line__text">{{ delegator.phone }}</span>
                  </a>
                  <div
                    v-if="delegator.last_request_date"
                    class="cs-line"
                  >
                    <span class="cs-line__icon cs-line__icon--success">
                      <feather-icon
                        icon="CalendarIcon"
                        size="12"
                      />
                    </span>
                    <span class="cs-line__text">Last request: {{ formatDate(delegator.last_request_date) }}</span>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <!-- Requests -->
        <section class="cs-card cs-card--span2">
          <header class="cs-card__head">
            <span class="cs-card__head-icon cs-card__head-icon--warning">
              <feather-icon
                icon="FileTextIcon"
                size="18"
              />
            </span>
            <div>
              <h3 class="cs-card__title">
                Requests
                <span class="cs-card__count">{{ requestsCount }}</span>
              </h3>
              <p class="cs-card__subtitle">
                Certification requests submitted by this company.
              </p>
            </div>
          </header>

          <div class="cs-card__body">
            <div
              v-if="!requests.length"
              class="cs-empty"
            >
              <div class="cs-empty__icon">
                <feather-icon
                  icon="FileMinusIcon"
                  size="26"
                />
              </div>
              <p class="mb-0 text-muted">
                No requests found for this company.
              </p>
            </div>

            <div
              v-else
              class="cs-req-grid"
            >
              <article
                v-for="request in requests"
                :key="request.id"
                class="cs-req"
              >
                <div class="cs-req__head">
                  <span class="cs-req__code">
                    <feather-icon
                      icon="HashIcon"
                      size="14"
                    />
                    {{ request.request_code }}
                  </span>
                  <span
                    v-if="request.source"
                    class="cs-req__source"
                  >
                    {{ request.source }}
                  </span>
                </div>

                <div class="cs-req__body">
                  <!--
                    Karim, 2 Sep: "the requests and the certificates are not showing
                    in the company... appearing only the request code." Fixed two
                    mismatches at once: `certificate` is a flat string, not
                    `{name:{en,ar}}` (nothing under this key was ever a translatable
                    object); and `application_status` was never a field this page's
                    data source produced — the real one is `status`.
                  -->
                  <div
                    v-if="request.certificate"
                    class="cs-req__cert"
                  >
                    <span class="cs-req__cert-icon">
                      <feather-icon
                        icon="AwardIcon"
                        size="15"
                      />
                    </span>
                    <span class="cs-req__cert-name">{{ request.certificate }}</span>
                  </div>

                  <!-- Which delegator this request belongs to — Karim, 2 Sep: "this
                       company has three delegators, so I know which request is with
                       the delegator. Which delegator owns the request?" -->
                  <div
                    v-if="request.delegator_name"
                    class="cs-req__delegator"
                  >
                    <a
                      v-if="request.delegator_id"
                      :href="contactHref(request.delegator_id)"
                      target="_blank"
                      rel="noopener"
                      class="cs-req__delegator-link"
                    >
                      <feather-icon
                        icon="UserIcon"
                        size="12"
                      />
                      {{ request.delegator_name }}
                    </a>
                    <span v-else>
                      <feather-icon
                        icon="UserIcon"
                        size="12"
                      />
                      {{ request.delegator_name }}
                    </span>
                  </div>

                  <!-- Status -->
                  <div
                    v-if="request.status"
                    class="cs-status"
                    :class="`cs-status--${statusVariant(request.status)}`"
                  >
                    <span class="cs-status__label">Status</span>
                    <span class="cs-status__value">
                      <feather-icon
                        :icon="statusIcon(request.status)"
                        size="13"
                      />
                      {{ request.status }}
                    </span>
                  </div>

                  <!-- Chips -->
                  <div class="cs-chips">
                    <!--
                      Karim, 3 Sep: "I saw you also synced the payment inside
                      the request details, so if you also get the invoice for
                      the paid one, it is attached with the request... when I
                      press the paid button, it appears." requests/{id}/invoice
                      already exists (InvoiceController, used elsewhere in the
                      admin panel) — nothing to build server-side, just wire it.
                    -->
                    <button
                      v-if="request.payment_status === 'paid'"
                      type="button"
                      class="cs-chip cs-chip--success cs-chip--clickable"
                      title="View invoice"
                      @click.stop="openInvoice(request)"
                    >
                      <feather-icon
                        icon="CreditCardIcon"
                        size="12"
                      />
                      paid
                      <feather-icon
                        icon="FileTextIcon"
                        size="11"
                        class="ml-05"
                      />
                    </button>
                    <span
                      v-else-if="request.payment_status"
                      class="cs-chip cs-chip--warning"
                    >
                      <feather-icon
                        icon="CreditCardIcon"
                        size="12"
                      />
                      {{ request.payment_status }}
                    </span>
                    <span
                      v-if="request.require_audit"
                      class="cs-chip cs-chip--info"
                    >
                      <feather-icon
                        icon="ClipboardIcon"
                        size="12"
                      />
                      Audit required
                    </span>
                    <span
                      v-if="request.jeem_request_type"
                      class="cs-chip cs-chip--primary"
                    >
                      <feather-icon
                        icon="BookmarkIcon"
                        size="12"
                      />
                      {{ request.jeem_request_type }}
                    </span>
                  </div>

                  <!-- Details -->
                  <ul
                    v-if="request.due_date || request.source_date || request.final_price || request.office"
                    class="cs-detail"
                  >
                    <li v-if="request.due_date">
                      <span>Due date</span>
                      <strong>{{ formatDate(request.due_date) }}</strong>
                    </li>
                    <li v-if="request.source_date">
                      <span>Source date</span>
                      <strong>{{ formatDate(request.source_date) }}</strong>
                    </li>
                    <li v-if="request.final_price">
                      <span>Final price</span>
                      <strong>{{ request.final_price }}</strong>
                    </li>
                    <li v-if="request.office">
                      <span>Office</span>
                      <strong>{{ request.office }}</strong>
                    </li>
                  </ul>

                  <ul
                    v-if="request.process_type"
                    class="cs-detail"
                  >
                    <li>
                      <span>Process type</span>
                      <strong>{{ request.process_type }}</strong>
                    </li>
                  </ul>
                </div>
              </article>
            </div>
          </div>
        </section>

        <!--
          CERTIFICATES — Karim, 2 Sep: "the requests and the certificates are not
          showing in the company."

          They were not shown at all: the earlier mapping put certificates into the
          REQUESTS slot, where the template looked for a different shape and found
          nothing. Both disappeared. Read live from the ERP view, so a certificate
          issued this morning is here.
        -->
        <section class="cs-card cs-card--span2">
          <header class="cs-card__head">
            <span class="cs-card__head-icon cs-card__head-icon--success">
              <feather-icon
                icon="AwardIcon"
                size="18"
              />
            </span>
            <div>
              <h3 class="cs-card__title">
                Certificates ({{ certificateRows.length }})
              </h3>
              <p class="cs-card__subtitle">
                <template v-if="company.nearestCertificate">
                  Next to expire: {{ company.nearestCertificate.name || company.nearestCertificate.number }}
                  on {{ company.nearestCertificate.expiresAt }}
                  ({{ company.nearestCertificate.daysToExpiry }} days)
                </template>
                <template v-else>
                  Issued to this company, plus applications still pending or
                  cancelled — so a facility with no certificate yet isn't shown as
                  if it never applied.
                </template>
              </p>
            </div>
          </header>

          <div class="cs-card__body">
            <p
              v-if="!certificateRows.length"
              class="text-muted mb-0"
            >
              No certificates or applications recorded for this company yet.
            </p>

            <table
              v-else
              class="cs-cert-table"
            >
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Certificate</th>
                  <th>Request #</th>
                  <th>Client</th>
                  <th>Source</th>
                  <th>Issued</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="cert in visibleCertificates"
                  :key="cert.key"
                  :class="{ 'cs-cert-table__pending': cert.placeholder }"
                >
                  <td>{{ cert.certificate_number || '— (pending)' }}</td>
                  <td>
                    {{ cert.certificate_name || (cert.sub_certificate_name && (cert.sub_certificate_name.en || cert.sub_certificate_name.ar)) || '—' }}
                    <span
                      v-if="cert.placeholder"
                      class="cs-chip cs-chip--warning ml-1"
                    >{{ cert.status || 'in progress' }}</span>
                  </td>
                  <td>{{ cert.request_code || '—' }}</td>
                  <td>{{ cert.clientName || '—' }}</td>
                  <td>{{ cert.source || '—' }}</td>
                  <td>{{ cert.issued_at || '—' }}</td>
                  <td :class="{ 'cs-cert-table__expired': isExpired(cert.expired_at) }">
                    {{ cert.expired_at || '—' }}
                  </td>
                </tr>
              </tbody>
            </table>

            <div
              v-if="certificateRows.length > visibleCertificates.length"
              class="cs-show-more"
            >
              <button
                type="button"
                class="cs-btn cs-btn--ghost"
                @click="certVisibleCount += 25"
              >
                Show 25 more ({{ visibleCertificates.length }} of {{ certificateRows.length }})
              </button>
            </div>
          </div>
        </section>

      </div>
    </template>

    <!-- Invoice modal — Karim, 3 Sep: "when I press the paid button, it appears." -->
    <div
      v-if="invoiceModal.open"
      class="cs-modal"
      @click.self="closeInvoice"
    >
      <div class="cs-modal__box">
        <header class="cs-modal__head">
          <h4 class="mb-0">
            Invoice
          </h4>
          <button
            type="button"
            class="cs-modal__close"
            @click="closeInvoice"
          >
            <feather-icon
              icon="XIcon"
              size="18"
            />
          </button>
        </header>
        <div class="cs-modal__body">
          <div
            v-if="invoiceModal.loading"
            class="text-center py-2"
          >
            <div
              class="spinner-border spinner-border-sm text-primary"
              role="status"
            />
          </div>
          <p
            v-else-if="invoiceModal.error"
            class="text-danger mb-0"
          >
            {{ invoiceModal.error }}
          </p>
          <div
            v-else-if="invoiceModal.data"
            class="cs-invoice"
          >
            <div class="cs-invoice__row">
              <span>Request</span>
              <strong>{{ invoiceModal.data.request_code }}</strong>
            </div>
            <div class="cs-invoice__row">
              <span>Invoice date</span>
              <strong>{{ formatDate(invoiceModal.data.invoice_date) }}</strong>
            </div>
            <div class="cs-invoice__row">
              <span>Payment method</span>
              <strong>{{ invoiceModal.data.payment_method || '—' }}</strong>
            </div>
            <div
              v-if="invoiceModal.data.office"
              class="cs-invoice__row"
            >
              <span>Office</span>
              <strong>{{ invoiceModal.data.office.name }}</strong>
            </div>
            <div
              v-if="invoiceModal.data.certificate"
              class="cs-invoice__row"
            >
              <span>Certificate</span>
              <strong>
                {{ invoiceModal.data.certificate.main }}
                <template v-if="invoiceModal.data.certificate.sub">
                  — {{ invoiceModal.data.certificate.sub }}
                </template>
              </strong>
            </div>
            <div
              v-if="invoiceModal.data.factory"
              class="cs-invoice__row"
            >
              <span>Factory</span>
              <strong>{{ invoiceModal.data.factory.name }}</strong>
            </div>
            <div
              v-if="invoiceModal.data.delegator"
              class="cs-invoice__row"
            >
              <span>Delegator</span>
              <strong>{{ invoiceModal.data.delegator.name }}</strong>
            </div>

            <div class="cs-invoice__prices">
              <div
                v-if="invoiceModal.data.price && invoiceModal.data.price.certificate"
                class="cs-invoice__row"
              >
                <span>Certificate price</span>
                <strong>{{ invoiceModal.data.price.certificate }}</strong>
              </div>
              <div
                v-if="invoiceModal.data.price && invoiceModal.data.price.tax"
                class="cs-invoice__row"
              >
                <span>Tax</span>
                <strong>{{ invoiceModal.data.price.tax }}</strong>
              </div>
              <div
                v-if="invoiceModal.data.price && invoiceModal.data.price.other_tax"
                class="cs-invoice__row"
              >
                <span>Other tax</span>
                <strong>{{ invoiceModal.data.price.other_tax }}</strong>
              </div>
              <div
                v-if="invoiceModal.data.price && invoiceModal.data.price.total"
                class="cs-invoice__row cs-invoice__row--total"
              >
                <span>Total</span>
                <strong>{{ invoiceModal.data.price.total }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable camelcase */
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'

const AVATAR_COLORS = [
  'linear-gradient(135deg, #7367f0, #9e95f5)',
  'linear-gradient(135deg, #00cfe8, #5be0ef)',
  'linear-gradient(135deg, #28c76f, #48da89)',
  'linear-gradient(135deg, #ff9f43, #ffb976)',
  'linear-gradient(135deg, #ea5455, #f08182)',
  'linear-gradient(135deg, #5a8dee, #7aa5f2)',
]

export default {
  name: 'ShowCompanyCRM',
  data() {
    return {
      loadingData: false,
      // Shown when the fetch fails, so a blank page never has to be interpreted.
      loadError: null,
      company: {},
      // Karim, 2 Sep: a facility can carry 1000+ certificates (one real one has
      // 1,434) — rendering them all in one unpaginated table is what made this
      // card look broken/"not showing". Reveal in batches instead.
      certVisibleCount: 25,
      savingTier: false,
      // Karim, 3 Sep: "when I press the paid button, it appears."
      invoiceModal: {
        open: false, loading: false, data: null, error: null,
      },
    }
  },
  computed: {
    // The ERP returns a translatable name object ({en, ar}); the CRM mirror stores
    // a plain string. Accept both — see the same note in CompaniesCRM.vue.
    companyName() {
      if (!this.company || !this.company.name) return ''
      if (typeof this.company.name === 'string') return this.company.name
      return this.company.name.en || this.company.name.ar || ''
    },
    secondaryName() {
      if (!this.company || !this.company.name) return ''
      if (typeof this.company.name === 'string') return ''
      const { ar } = this.company.name
      if (ar && ar !== this.companyName) return ar
      return ''
    },
    postalCode() {
      return this.company.postal_code || this.company.zip_code || ''
    },
    delegators() {
      return Array.isArray(this.company.delegators_info) ? this.company.delegators_info : []
    },
    requests() {
      return this.company.requests_info && Array.isArray(this.company.requests_info.requests)
        ? this.company.requests_info.requests
        : []
    },
    /** Certificates issued to this facility, newest expiry first. */
    certificates() {
      const list = Array.isArray(this.company.certificates_info) ? this.company.certificates_info : []
      return [...list].sort((a, b) => String(b.expired_at || '').localeCompare(String(a.expired_at || '')))
    },
    /**
     * Karim, 2 Sep: "the clients with no certificates should at least have
     * cancelled or pending certificates still going, then the certificate number
     * can be empty and filled out later. Any request number must be seen with
     * certificate number and client name and facility together." Merges the real
     * certificates with a placeholder row for every request that hasn't (yet)
     * produced one, and carries the delegator's name on every row — real or
     * placeholder — a company with several delegators can otherwise not tell
     * whose certificate/application is whose.
     */
    certificateRows() {
      const clientNames = {}
      this.delegators.forEach(d => { clientNames[d.id] = d.name })

      const real = this.certificates.map(c => ({
        key: `cert-${c.erp_certificate_id || c.id || c.request_code}`,
        placeholder: false,
        certificate_number: c.certificate_number,
        certificate_name: c.certificate_name,
        sub_certificate_name: c.sub_certificate_name,
        issued_at: c.issued_at,
        expired_at: c.expired_at,
        request_code: c.request_code,
        source: c.source,
        clientName: clientNames[c.erp_user_id] || null,
        status: null,
      }))

      const covered = new Set(real.map(c => c.request_code).filter(Boolean))

      const placeholders = this.requests
        .filter(r => r.request_code && !covered.has(r.request_code))
        .map(r => ({
          key: `req-${r.id}`,
          placeholder: true,
          certificate_number: null,
          certificate_name: r.certificate,
          sub_certificate_name: null,
          issued_at: null,
          expired_at: null,
          request_code: r.request_code,
          source: r.source,
          clientName: r.delegator_name,
          status: r.status,
        }))

      return [...real, ...placeholders]
    },
    visibleCertificates() {
      return this.certificateRows.slice(0, this.certVisibleCount)
    },

    requestsCount() {
      if (this.company.requests_info && typeof this.company.requests_info.count === 'number') {
        return this.company.requests_info.count
      }
      return this.requests.length
    },
    activityList() {
      const activity = this.company && this.company.activity
      if (!activity) return []
      const raw = activity.en || activity.ar
      if (!raw || typeof raw !== 'string') return []
      return raw
        .split(/[;,]/)
        .map(item => item.trim())
        .filter(Boolean)
    },
    crStatus() {
      const endDate = this.company.CR_end_date
      if (!endDate) {
        return { variant: 'none', label: 'No CR date', icon: 'HelpCircleIcon' }
      }
      const date = new Date(endDate)
      if (Number.isNaN(date.getTime())) {
        return { variant: 'none', label: 'No CR date', icon: 'HelpCircleIcon' }
      }
      const now = new Date()
      const days = Math.ceil((date - now) / (1000 * 60 * 60 * 24))
      if (days < 0) return { variant: 'expired', label: 'CR expired', icon: 'AlertTriangleIcon' }
      if (days <= 60) return { variant: 'soon', label: `CR · ${days}d left`, icon: 'ClockIcon' }
      return { variant: 'valid', label: 'CR valid', icon: 'CheckCircleIcon' }
    },
    avatarColor() {
      const id = Number(this.$route.params.id) || (this.companyName ? this.companyName.length : 0)
      return AVATAR_COLORS[id % AVATAR_COLORS.length]
    },
  },
  mounted() {
    this.showCompany()
  },
  methods: {
    colorFor(key) {
      const num = Number(key) || 0
      return AVATAR_COLORS[num % AVATAR_COLORS.length]
    },
    statusVariant(status) {
      const s = String(status || '').toLowerCase()
      // Karim, 3 Sep: "terminated is equal to certificate issued in our
      // system" — Jeem/Saber's word for a finished, successful request is
      // "Terminated" (10,263 of ~57,500 requests — the single most common
      // closed status). It was grouped with reject/cancel/fail as a failure,
      // which is backwards: it belongs with issued/complete/success.
      if (/(reject|cancel|fail|declin|refus|error)/.test(s)) return 'danger'
      if (/(approve|complete|done|issued|accept|success|paid|finish|terminat)/.test(s)) return 'success'
      if (/(follow|comment|study|review|pending|wait|progress|process|hold)/.test(s)) return 'warning'
      return 'info'
    },
    statusIcon(status) {
      const variant = this.statusVariant(status)
      const map = {
        danger: 'XCircleIcon',
        success: 'CheckCircleIcon',
        warning: 'ClockIcon',
        info: 'InfoIcon',
      }
      return map[variant] || 'InfoIcon'
    },
    formatDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return typeof value === 'string' ? value.split('T')[0] : value
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    initials(name) {
      if (!name) return '??'
      return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')
    },
    isExpired(date) {
      if (!date) return false
      return new Date(date) < new Date()
    },

    goBack() {
      this.$router.push({ name: 'crm-system-companies' }).catch(() => {})
    },
    /**
     * Karim, 3 Sep: "I saw you also synced the payment inside the request
     * details, so if you also get the invoice for the paid one, it is
     * attached with the request... when I press the paid button, it
     * appears." requests/{id}/invoice already exists (InvoiceController) —
     * used elsewhere in the admin panel, just never linked from the CRM.
     */
    async openInvoice(request) {
      this.invoiceModal = {
        open: true, loading: true, data: null, error: null,
      }
      try {
        const res = await axios.get(`requests/${request.id}/invoice`)
        const body = decryptData(res.data.payload)
        this.invoiceModal.data = (body && body.data && body.data.data && body.data.data.invoice) || null
        if (!this.invoiceModal.data) {
          this.invoiceModal.error = 'No invoice data returned for this request.'
        }
      } catch (e) {
        this.invoiceModal.error = 'Could not load the invoice for this request.'
      } finally {
        this.invoiceModal.loading = false
      }
    },
    closeInvoice() {
      this.invoiceModal = {
        open: false, loading: false, data: null, error: null,
      }
    },
    /**
     * Karim, 2 Sep: "when I try to open the company itself, I want to press on the
     * delegator and he opens in a new tab." A plain resolved href (not a click
     * handler that $router.push()es) so target="_blank" and middle-click both work
     * the way a normal link does.
     */
    contactHref(id) {
      if (!id) return null
      return this.$router.resolve({ name: 'crm-system-contact-show', params: { id } }).href
    },
    /** Karim, 2 Sep: "add client rating" — gold / silver / bronze, or cleared. */
    async setTier(value) {
      const tier = value || null
      this.savingTier = true
      try {
        await axios.post(`crm/companies/${this.$route.params.id}/tier`, { tier })
        this.company = { ...this.company, tier }
      } catch (e) {
        // Leave company.tier untouched — the select reverts to it on re-render.
      } finally {
        this.savingTier = false
      }
    },
    /**
     * Load the company from the CRM, not from the raw ERP endpoint.
     *
     * Karim, 2 Sep: "when in companies and I try to enter a company, it doesn't give
     * any data inside — no data inside the company, but outside there numbers."
     *
     * The cause: this called `factory/{id}` and read `.data.factory`. The COMPANIES
     * LIST was moved onto the CRM's own scoped endpoint months ago, but this detail
     * page was left pointing at the old ERP one, whose response has a different shape
     * — so `.data.factory` came back undefined and every field rendered blank. The
     * list worked, the detail did not, which is exactly what he saw.
     *
     * It is the same failure as the dead certificate column: a data source changed,
     * and the screen showed emptiness instead of an error.
     */
    async showCompany() {
      if (!this.$route.params.id) return
      this.loadingData = true
      this.loadError = null
      try {
        const res = await axios.get(`crm/companies/${this.$route.params.id}`)
        const company = decryptData(res.data.payload).data || {}

        this.company = {
          ...company,
          // The page shows the ERP facility id, which is what everyone quotes to
          // each other. `company.id` is the CRM row's own id and means nothing here.
          id: company.erp_facility_id ?? company.id,
          // The template was written against the ERP payload's names. Map rather than
          // rewrite the whole template: fewer places to get wrong today.
          /**
           * The template expects the ERP payload's shapes:
           *   delegators_info : a flat array of people
           *   requests_info   : an OBJECT — { requests: [...], count: N }
           *
           * My first mapping fed `company.certificates` (a flat array) into
           * requests_info, so `requests_info.requests` was undefined and both the
           * request list and its count rendered empty. Certificates were not shown
           * anywhere either — they went into the requests slot and vanished.
           */
          /**
           * Two possible sources, two different shapes — normalise both to one.
           * `company.people` (CrmAccountActivityService::clientsForFacility, live ERP
           * read) carries `clientId` / `lastRequestAt`; the `company.clients`
           * fallback (raw CrmClient rows) carries `erp_user_id` and has no request
           * activity at all. The template read neither correctly before — it
           * expected `delegator.id` (routing) and `delegator.last_request_date`
           * (Karim, 2 Sep: the last-request date was never appearing), and got
           * `undefined` from both possible sources every time.
           */
          delegators_info: (company.people?.length ? company.people : (company.clients || [])).map(p => ({
            id: p.clientId ?? p.erp_user_id ?? null,
            name: p.name,
            email: p.email,
            phone: p.phone,
            requests: p.requests ?? null,
            last_request_date: p.lastRequestAt ?? null,
          })),
          requests_info: {
            /**
             * Reshape to what the template reads. It was written against the ERP
             * payload (request_code, certificate.name); CrmAccountActivityService
             * returns a flatter shape (code, certificate as a plain string).
             * Mapping here beats rewriting the template — one place to get wrong.
             */
            requests: (company.requests || []).map(r => ({
              id: r.id,
              request_code: r.code,
              source: r.source,
              status: r.status,
              created_at: r.createdAt,
              open: r.open,
              // Flat string — CrmAccountActivityService already resolves the
              // translatable name server-side. No `.name.en/.ar` nesting exists.
              certificate: r.certificate,
              process_type: r.processType,
              payment_status: r.paymentStatus,
              final_price: r.finalPrice,
              require_audit: r.requireAudit,
              due_date: r.dueDate,
              source_date: r.sourceDate,
              office: r.office,
              jeem_request_type: r.jeemRequestType,
              // Karim, 2 Sep: "this company has three delegators — which delegator
              // owns the request?" The request now carries who submitted it.
              delegator_id: r.delegatorId,
              delegator_name: r.delegatorName,
            })),
            count: company.requestSummary?.total ?? (company.requests || []).length,
          },
          // Certificates get their own slot, and the summary drives the header.
          certificates_info: company.certificates || [],
          requestSummary: company.requestSummary || {},
          nearestCertificate: company.nearestCertificate || null,
        }
      } catch (e) {
        // Say what happened. A silent catch here is what let the blank page look
        // like "no data" rather than "the request failed".
        this.company = {}
        this.loadError = (e && e.response && e.response.status === 404)
          ? 'This company is not in the CRM yet. Run crm:sync-clients, or it may belong to another salesperson.'
          : 'Could not load this company. Please retry.'
      } finally {
        this.loadingData = false
      }
    },
  },
}
</script>

<style lang="scss" src="./show-company-crm.scss" scoped></style>
